import { Suspense, useEffect, useState } from "react";

import { Button, Divider, FormControl, FormErrorMessage, FormLabel, Input, Radio, RadioGroup, useDisclosure, useToast } from "@chakra-ui/react"
import { Field, FieldProps, Form, Formik, FormikProps } from "formik"
import { useLocation, useNavigate } from "react-router-dom"
import { HttpStatusCode } from "axios";
import { format } from "date-fns";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import ArrowIcon from "@/assets/ArrowIcon";
import ModalConfirmation from "@/components/Modals/ModalConfirmation";
import ModalInvoicePreview from "@/components/Modals/ModalInvoicePreview";
import { CLIENT_ID_SETTINGS, CURRENCY, DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, DURATION_TOAST, FORMAT_ADD_DATE, INVOICE_STATUS, PAYMENT_STATUS, PPN_PERCENTAGE, ROLES, COLORS } from "@/common/constants";
import CurrencyInput from "@/components/Fields/CurrencyInput";
import { useAppSelector } from "@/redux/hooks";
import { RootState } from "@/redux/store";

import '../customDatepicker.css';
import '../SireInvoiceForm.css'
import { Option } from "@/utils/select";
import Loader from "@/components/Layouts/Loader";
import TableSireReviewSimple, { SireDataSimple } from "@/components/Tables/TableSireReviewSimple";
import MonthSelector from "@/components/Dashboard/MonthSelector";
import EditFile from "@/components/Fields/EditFile";
import { getListSireReviewsSimpleByInvoice } from "@/services/InvoiceSireReviewReviewerService";
import SelectShip from "@/components/Selects/SelectShip";
import SelectInvoices from "@/components/Selects/SelectInvoices";
import { getListSireReviewRequestingCompany } from "@/services/sireReviewService";
import { getListSireReviewsRequestorOrderNumbers, getListSireReviewsSimpleByInvoiceAndRequestor, getListSireReviewsSimpleForRequestor, getSingleSireReviewDetailForRequestor, getSireReviewRequestorInvoiceDetails, postCreateInvoicesSireReviewRequestor, postUpdateInvoicesSireReviewRequestorFinance, postUpdateSireReviewRequestorInvoice, SireReviewRequestorInvoiceDetail, SireReviewSimpleRequestor, SireReviewSimpleRequestorSent } from "@/services/InvoiceSireReviewRequestorService";
import { invoiceSireReviewRequestorSchema } from "@/schemas/invoiceSireReviewRequestorSchema";
import { getExchangeRateLast7DaysAverage, getExchangeRateMonthlyAverage } from "@/services/exchangeRateService";
import { getSettingsById } from "@/services/settingService";

type FormContents = {
    companyID: string,
    invoiceID: string,
    invoiceDate: string,
    isMonthly: boolean,
    reviewID: string,
    valueOfUSD: number,
    reviewCost: number,
    fakturFile: File | null,
    companyName: string,
    downloadCost: number,
};

const AddInvoicesSireRequestor = () => {
    // Helper to get invoice details from listSireReview
    // const getInvoiceDetailsFromList = () => {
    //     return Array.isArray(listSireReview) ? listSireReview : [];
    // };
    const navigate = useNavigate();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const role = useAppSelector((state: RootState) => state.account.roleName);
    const isAdmin = role === ROLES.SUPER_ADMIN;
    const now = new Date();
    const currentMonth = now.getMonth() + 1; // getMonth() returns 0-11
    const currentYear = now.getFullYear();

    const location = useLocation();
    const { id } = location.state || {};

    const initial: FormContents = {
        invoiceID: '',
        invoiceDate: '',
        valueOfUSD: 0,
        reviewCost: 0,
        fakturFile: null,
        isMonthly: true,
        reviewID: '',
        companyID: '',
        companyName: '',
        downloadCost: 0,
    };

    const [confirmationText, setConfirmationTest] = useState("");
    const [confirmationTitleButton, setConfirmationTitleButton] = useState("");
    const [dataAdd, setDataAdd] = useState<FormContents>(initial);
    const [isDraft, setIsDraft] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewInvoiceData, setPreviewInvoiceData] = useState<Record<string, any> | null>(null);
    const [listCompanyName, setListCompanyName] = useState<Option[]>([]);
    const [title, setTitle] = useState("");
    const [isMonthly, setIsMonthly] = useState<boolean>(true);
    const [selectedMonth, setSelectedMonth] = useState<number>((currentMonth === 1) ? 12 : (currentMonth - 1));
    const [selectedYear, setSelectedYear] = useState<number>((currentMonth === 1) ? (currentYear - 1) : currentYear);
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
    const [reviewCost, setReviewCost] = useState<number>(0);
    const [downloadCost, setDownloadCost] = useState<number>(0);

    const [listSireReview, setListSireReview] = useState<SireDataSimple[]>([]);
    const [listOrderNumbers, setListOrderNumbers] = useState<Option[]>([]);
    const [totalReviews, setTotalReviews] = useState<number>(0);
    const [totalSecondDownload, setTotalSecondDownload] = useState<number>(0);
    const [subtotal, setSubtotal] = useState<number>(0);
    const [taxPercent, setTaxPercent] = useState<number>(PPN_PERCENTAGE);
    const [taxAmount, setTaxAmount] = useState<number>(0);
    const [totalCost, setTotalCost] = useState<number>(0);
    const [currentPageNumber, setCurrentPageNumber] = useState(DEFAULT_PAGE_NUMBER);
    const [currentPageSize, setCurrentPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [totalPageNumber, setTotalPageNumber] = useState(1);
    const [kurs, setKurs] = useState<number>(0);
    const [singleReviewId, setSingleReviewId] = useState<string>('');
    const [companyId, setCompanyId] = useState<string>('');

    const [initialContents, setInitialContents] = useState<FormContents>(initial)

    const handleCancel = () => {
        onOpen();
        setTitle("Cancel Creating Invoice?");
        setConfirmationTest("Are you sure you want to cancel?");
        setConfirmationTitleButton("Yes");
    }

    // Show preview modal instead of confirmation
    const handleSubmit = async (data: FormContents) => {
        if (!isMonthly && !singleReviewId){
            showToast({ message: 'Please select an order number.' });
            return;
        }
        setDataAdd(data);
        // Generate invoice data for preview (same as handleConfirm)
        const obj = await generateDataAdd();
        setPreviewInvoiceData(obj);
        setIsPreviewOpen(true);
    }

    // Called when user confirms in preview modal
    const handlePreviewConfirm = () => {
        setIsPreviewOpen(false);
        setIsDraft(false);
        setTitle("Create Invoice?");
        setConfirmationTest("Are you sure you want to create?");
        setConfirmationTitleButton("Create Invoice");
        onOpen();
        setIsSubmitting(true);
    };

    // Helper to build invoice preview data from generated invoice object
    const buildInvoicePreviewData = () => {
        if (!previewInvoiceData) return null;
        // Map previewInvoiceData to modal fields
        let details = listSireReview;
        if (!Array.isArray(details)) {
            details = [];
        }

        // Helper to format date string to 'yyyy-MM-dd'
        const formatDate = (dateStr: string) => {
            if (!dateStr || !dateStr.match) return '';
            // Check if it's ISO string with time
            const match = dateStr.match(/^(\d{4}-\d{2}-\d{2})T/);
            if (match) return match[1];
            return dateStr;
        };

        return {
            requestorName: previewInvoiceData.requestorName ?? '',
            invoiceNumber: dataAdd.invoiceID || '',
            invoiceDate: formatDate(dataAdd.invoiceDate ?? ''),
            orderNumber: previewInvoiceData.reviewID ?? previewInvoiceData.orderNumber ?? '',
            costPerReview: previewInvoiceData.costReview ?? '',
            totalReviews: previewInvoiceData.totalReview ?? '',
            costPerDownload: previewInvoiceData.costDownload ?? '',
            total2ndDownload: previewInvoiceData.totalSecondDownload ?? totalSecondDownload,
            exchangeRate: previewInvoiceData.kurs ?? kurs,
            subtotal: previewInvoiceData.subTotal ?? subtotal,
            tax: previewInvoiceData.ppnPrice ?? taxAmount,
            totalCost: previewInvoiceData.totalAmount ?? totalCost,
            details,
        };
    };

    // Add previewMode flag to generate preview data for modal
    const generateDataAdd = async (previewMode = false) => {
        const companyData = listCompanyName.find((x: any) => x.value === companyId);

        let listResponse: SireReviewSimpleRequestorSent[] = [];
        if (isMonthly){
            if (id){
                listResponse = await getListSireReviewsSimpleByInvoiceAndRequestor(id, companyId, 1, totalReviews);
            } else {
                const res = await getListSireReviewsSimpleForRequestor({
                    requestorId: companyId,
                    month: selectedMonth,
                    year: selectedYear,
                    pageNumber: 1,
                    pageSize: totalReviews,
                });
                const originalData = res.data as SireReviewSimpleRequestor[];
                const transformedData = originalData.map((review) => ({
                    sireReviewId: review.id,
                    sequenceId: review.sequenceId,
                    requestorId: review.requestorId ?? '',
                    requestorName: companyData?.label ?? '',
                    addAldPertamina: review.addAldPertamina,
                    reviewDate: review.reviewDate,
                    vesselId: review.vesselId,
                    vesselName: review.vesselName,
                    vesselType: review.vesselType,
                    imoNumber: review.imoNumber,
                })) as SireReviewSimpleRequestorSent[];
                listResponse = transformedData;
            }
        } else {
            const review = listSireReview[0];
            listResponse = [{
                sireReviewId: review.id,
                sequenceId: review.sequenceId,
                requestorId: review.requestorId ?? '',
                requestorName: companyData?.label ?? '',
                addAldPertamina: review.addaldPertamina,
                reviewDate: (typeof(review.reviewDate) === 'string') ? review.reviewDate : review.reviewDate.toISOString(),
                vesselId: review.vesselId,
                vesselName: review.vesselName,
                vesselType: review.vesselType,
                imoNumber: review.imoNumber,
            }]
        }

        const data = {
            requestorId: companyId,
            requestorName: companyData?.label ?? '',
            invoiceNumber: '',
            invoiceDate: dataAdd.invoiceDate ? format(dataAdd.invoiceDate, FORMAT_ADD_DATE) : '',
            isMonthly: isMonthly,
            reviewMonth: isMonthly ? selectedMonth : null,
            reviewYear:  isMonthly ? selectedYear : null,
            totalReview: totalReviews,
            costReview: reviewCost,
            kurs: kurs,
            subTotal: subtotal,
            ppn: taxPercent,
            ppnPrice: taxAmount,
            totalAmount: totalCost,
            invoiceStatus: isDraft ? INVOICE_STATUS.DRAFT : INVOICE_STATUS.FINANCE,
            paymentStatus: PAYMENT_STATUS.UNPAID,
            notes: '',
            costDownload: downloadCost,
            invoiceDetails: listResponse,
            totalSecondDownload: totalSecondDownload,
        };

        if (previewMode) {
            return { id,...data };
        }

        if (id){
            return { id, ...data };
        } else {
            return { ...data, invoiceDetails: listResponse }
        }
    };

    const generateDataAddFinance = () => {
        const data = new FormData();
        data.append('Id', id);
        data.append('InvoiceNumber', dataAdd.invoiceID || '');
        data.append('InvoiceDate', dataAdd.invoiceDate ? format(dataAdd.invoiceDate, FORMAT_ADD_DATE) : '');
        data.append('RequestorId', companyId);
        data.append('CostReview', String(reviewCost));
        data.append('PPN', String(taxPercent));
        data.append('PPNPrice', String(taxAmount));
        data.append('SubTotal', String(subtotal));
        data.append('TotalAmount', String(totalCost));
        uploadedFiles.map((file) => {
            data.append('file', file);
        });
        return data;
    };

    const handleConfirm = async () => {
        onClose();

        if (title === "Cancel Creating Invoice?") {
            navigate('/manage-invoices/sire-requestor');
            return;
        }

        const obj = await generateDataAdd();
        let response;
        if (id){
            if (role === ROLES.FINANCE){
                response = await postUpdateInvoicesSireReviewRequestorFinance(generateDataAddFinance());
            } else {
                response = await postUpdateSireReviewRequestorInvoice(obj);
            }
        } else {
            response = await postCreateInvoicesSireReviewRequestor(obj);
        }
        
        setIsSubmitting(false);
        if (response?.status === HttpStatusCode.Ok) {
            toast({
                title: 'Invoice created.',
                description: "We've created the invoice for you.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });

            setTimeout(() => {
                navigate('/manage-invoices/sire-requestor')
            }, 1000)
        } else {
            toast({
                title: 'Inspector failed to create.',
                description: "We could not create the invoice for you.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    const handleConfirmToCancel = () => {
        setIsSubmitting(false);
        onClose();
    };

    const handleChangeMonth = (month: number, year: number, props: FormikProps<FormContents>) => {
        props.setFieldValue('reviewMonth', month);
        props.setFieldValue('reviewYear', year);
        setSelectedMonth(month);
        setSelectedYear(year);
    };

    const showToast = (error: { code?: string; message?: string }) => {
        toast({
            title: error?.code,
            description: error?.message,
            status: 'error',
            duration: DURATION_TOAST,
            isClosable: true,
        });
    };

    const getListSireReviews = async () => {
        setIsLoading(true);

        try {
            let response
            if (id){
                response = await getListSireReviewsSimpleByInvoice(id, currentPageNumber, currentPageSize);
            } else {
                response = await getListSireReviewsSimpleForRequestor({
                    requestorId: companyId,
                    month: selectedMonth,
                    year: selectedYear,
                    pageNumber: currentPageNumber,
                    pageSize: currentPageSize,
                });
            }
            
            //console.log(response)
            
            if (response?.status === HttpStatusCode.Ok) {
                if (response.totalItem === 0){
                    setListSireReview([]);
                    setTotalReviews(0)
                    setTotalSecondDownload(0);
                    setTotalPageNumber(0);
                    setHasNextPage(false);
                    return
                }
                const originalData = response.data as SireReviewSimpleRequestor[];
                const transformedData: SireDataSimple[] = originalData.map((data) => ({
                    ...data,
                    addaldPertamina: data.addAldPertamina,
                    requestor: data.requestorName,
                }))
                setListSireReview(transformedData);
                setTotalReviews(response?.totalItem)
                setTotalSecondDownload(response?.data?.filter((item: any) => item.isSecondDownload).length || 0);
                setTotalPageNumber(response?.totalPage);
                setHasNextPage(response?.hasNextPage);
            } else {
                showToast(response?.error);
                setListSireReview([]);
                setTotalReviews(0)
                setTotalSecondDownload(4);
                setTotalPageNumber(0);
                setHasNextPage(false);
            }
        } catch {
            showToast({ code: 'Error', message: 'Failed to fetch data' });
            setListSireReview([]);
            setTotalReviews(0)
            setTotalPageNumber(0);
            setHasNextPage(false);
        } finally {
            setIsLoading(false);
        }
    };

    const getSingleSireReviewDetail = async () => {
        setIsLoading(true);
        const response = await getSingleSireReviewDetailForRequestor(companyId, singleReviewId);
        if (response.data){
            const originalData = response.data as SireReviewSimpleRequestor[];
            const transformedData: SireDataSimple[] = originalData.map((data) => ({
                ...data,
                addaldPertamina: data.addAldPertamina,
                requestor: data.requestorName,
            }));
            setListSireReview(transformedData);
            setTotalReviews(1)
            setTotalPageNumber(1);
            setHasNextPage(false);
        } else {
            showToast({ code: 'Error', message: 'Failed to fetch data' });
            setListSireReview([]);
            setTotalReviews(0)
            setTotalPageNumber(0);
            setHasNextPage(false);
        }
        setIsLoading(false)
    }

    const getListCompanies = async () => {
        try {
            const res = await getListSireReviewRequestingCompany();
            if (res.totalItem === 0){
                showToast({ code: 'No suitable SIRE reviews', message: 'Please select a different company or month.' });
                setListCompanyName([]);
            }
            const listCompanyRaw = res.data as {id: string, name: string}[];
            const listCompany: Option[] = listCompanyRaw.map((company) => ({
                value: company.id,
                label: company.name,
            }));
            setListCompanyName(listCompany);
        } catch {
            showToast({ code: 'Error', message: 'Failed to fetch data' });
            setListCompanyName([]);
        }
    };

    const getAverageExchangeRate = async () => {
        let response: {status: number, data: number, message: number};

        if (isMonthly){
            response = await getExchangeRateMonthlyAverage(selectedMonth, selectedYear);
            setKurs(response.data);
        } else if (listSireReview.length > 0) {
            response = await getExchangeRateLast7DaysAverage(String(listSireReview[0].created));
            setKurs(response.data);
        }
    };

    const getListOrderNumbers = async (companyId: string) => {
        const response = await getListSireReviewsRequestorOrderNumbers(companyId);
        if (response.data){
            const dataList = response.data as { id: string, sequenceId: string }[];
            const result: Option[] = dataList.map((data) => ({
                value: data.id,
                label: data.sequenceId,
            }));
            setListOrderNumbers(result);
        }
    }

    const handleDrop = (acceptedFiles: File[], fieldName: string, setFieldValue: (fieldName: string, value: File[]) => void) => {
        setUploadedFiles(acceptedFiles);
        setFieldValue(fieldName, acceptedFiles);
    };

    const handleChangeReviewCost = (value: number, setFieldValue: (fieldName: string, value: number) => void) => {
        setFieldValue(`reviewCost`, value);
        setReviewCost(value);
    };

    const handleChangeSecondDownloadCost = (value: number, setFieldValue: (fieldName: string, value: number) => void) => {
        setFieldValue(`downloadCost`, value);
        setDownloadCost(value);
    };

    const handleChangeKurs = (value: number, setFieldValue: (fieldName: string, value: number) => void) => {
        setFieldValue(`valueOfUSD`, value);
        //setKurs(value);
    };

    const getInvoiceDetails = async (id: string) => {
        setIsSubmitting(true);
        const res = await getSireReviewRequestorInvoiceDetails(id);
        if (res?.data){
            const data = res.data as SireReviewRequestorInvoiceDetail;
            const initial = {
                invoiceID: data.invoiceNumber ?? '',
                invoiceDate: data.invoiceDate,
                reviewCost: data.costReview,
                companyID: data.requestorId,
                companyName: data.requestorName,
                isMonthly: data.isMonthly,
                valueOfUSD: data.kurs,
                reviewID: '',
                fakturFile: null,
                downloadCost: data.costDownload,
            }
            setInitialContents(initial);
            
            const originalData = data.invoiceDetails as SireReviewSimpleRequestor[];
            const transformedData: SireDataSimple[] = originalData.map((data) => ({
                ...data,
                addaldPertamina: data.addAldPertamina,
                requestor: data.requestorName,
            }));
            await getListOrderNumbers(data.requestorId);
            setListSireReview(transformedData);
            if (!data.isMonthly){
                setSingleReviewId(originalData[0].sireReviewId);
            }
            setCompanyId(data.requestorId);
            setReviewCost(data.costReview);
            setTotalReviews(data.totalReview);
            setKurs(data.kurs);
            setTotalCost(data.totalAmount);
            setIsMonthly(data.isMonthly);
            if (data.isMonthly && data.reviewMonth && data.reviewYear){
                setSelectedMonth(data.reviewMonth ?? currentMonth);
                setSelectedYear(data.reviewYear ?? currentYear);
            }
            if (data.invoiceStatus !== INVOICE_STATUS.DRAFT){
                setTaxPercent(data.ppn);
                setTaxAmount(data.ppnPrice);
            }
        }
        setIsSubmitting(false);
        setIsLoading(false)
    };

    const getTaxPercent = async () => {
        const res = await getSettingsById(CLIENT_ID_SETTINGS);
        if (res.data){
            setTaxPercent(res.data.ppn ?? PPN_PERCENTAGE);
        }
    };

    useEffect(() => {
        getListCompanies();
        if (id){
            getInvoiceDetails(id);
        }
        if (isDraft) {
            getTaxPercent();
        }
    }, []);

    useEffect(() => {
        if (companyId && isMonthly && !id){
            getListSireReviews();
        }
    }, [selectedMonth, selectedYear, companyId, isMonthly]);

    useEffect(() => {
        if (!id){
            getAverageExchangeRate();
        }
    }, [isMonthly, selectedMonth, selectedYear, listSireReview, singleReviewId]);

    useEffect(() => {
        if (!id && companyId && singleReviewId && !isMonthly){
            getSingleSireReviewDetail();
        }
    }, [isMonthly, singleReviewId])

    useEffect(() => {
        if (!isMonthly){
            getListOrderNumbers(companyId)
        }
    }, [isMonthly, companyId])

    useEffect(() => {
        if (!id && companyId && isMonthly){
            getListSireReviews();
        }
    }, [currentPageNumber, currentPageSize]);

    useEffect(() => {
        setSubtotal((reviewCost * totalReviews + downloadCost * totalSecondDownload) * kurs);
    }, [reviewCost, totalReviews, downloadCost, totalSecondDownload, kurs])

    useEffect(() => {
        const tax = subtotal * (taxPercent / 100);
        setTaxAmount(tax);
        setTotalCost(subtotal + tax)
    }, [subtotal, taxPercent])

    const handleChangeReview = (e: Option, props: FormikProps<FormContents>) => {
        setSingleReviewId(e.value)
        props.setFieldValue('reviewID', e.value)
    };

    const handleSelectCompany = (e: any, props: FormikProps<FormContents>) => {
        setCompanyId(e)
        props.setFieldValue('companyName', e.value)
    };

    return (
        <>
            <ModalConfirmation isOpen={isOpen} onClose={onClose} title={title} handleCancel={handleConfirmToCancel} handleConfirm={handleConfirm} confirmationText={confirmationText} confirmButtonText={confirmationTitleButton} />
            <div className="form-container">
                <Formik
                    initialValues={initialContents}
                    validationSchema={() => invoiceSireReviewRequestorSchema(role)}
                    onSubmit={(values) => {
                        handleSubmit(values);
                    }}
                    enableReinitialize
                >
                    {(props: FormikProps<FormContents>) => (
                        <Form>
                            <Field name='companyName'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={!!(form.errors.companyName && form.touched.companyName)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Requestor Name{isAdmin && <span style={{ color: 'red' }}>*</span>}</FormLabel>
                                            <div className="form-input-container">
                                                <SelectShip
                                                    placeholder="Choose Requestor"
                                                    name='companyName'
                                                    handleChange={(option) => {
                                                        if (option) handleSelectCompany(option.value, props);
                                                        else handleSelectCompany('', props);
                                                    }}
                                                    value={companyId}
                                                    options={listCompanyName}
                                                    hasError={!!(form.errors.companyName && form.touched.companyName)}
                                                    isDisabled={isSubmitting || !isAdmin}
                                                />
                                            </div>
                                        </div>
                                        <FormErrorMessage>
                                            {typeof form.errors.companyName === 'string'
                                                ? form.errors.companyName
                                                : Array.isArray(form.errors.companyName)
                                                    ? form.errors.companyName.join(', ')
                                                    : form.errors.companyName
                                                        ? JSON.stringify(form.errors.companyName)
                                                        : ''}
                                        </FormErrorMessage>
                                    </FormControl>
                                )}
                            </Field>
                            {role === ROLES.FINANCE && <Field name='invoiceID'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.invoiceID && form.touched.invoiceID)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Invoice Number<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Invoice Number' className="form-input" isDisabled={isSubmitting} />
                                                <FormErrorMessage>{String(form.errors.invoiceID)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>}
                            <Field name='invoiceDate'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.invoiceDate && form.touched.invoiceDate)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Invoice Date{isAdmin && <span style={{ color: 'red' }}>*</span>}</FormLabel>
                                            <div style={{
                                                width: '100%',
                                                height: '2.5rem',
                                                fontSize: '1rem',
                                                paddingInlineStart: '1rem',
                                                paddingInlineEnd: '1rem',
                                                borderRadius: '0.375rem',
                                                minWidth: '0px',
                                                outline: '2px solid transparent',
                                                outlineOffset: '2px',
                                                position: 'relative',
                                                appearance: 'none',
                                                transitionProperty: 'background-color, border-color, color, fill, stroke, opacity, box-shadow, transform',
                                                paddingTop: '0.35rem',
                                                marginBottom: (form.errors.invoiceDate && form.touched.invoiceDate) ? '1.25rem' : 0,
                                                border: form.errors.invoiceDate && form.touched.invoiceDate ? '2px solid red' : '1px solid #e5e7e9'
                                            }}>
                                                <DatePicker
                                                    id="invoiceDate"
                                                    selected={field.value as Date | null}
                                                    onChange={(date: Date | null) => props.setFieldValue('invoiceDate', date)}
                                                    dateFormat="dd-MM-YYYY"
                                                    placeholderText="Invoice Date"
                                                    readOnly={isSubmitting}
                                                />
                                                <FormErrorMessage>{String(form.errors.invoiceDate)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='isMonthly'>
                                {() => (
                                    <FormControl style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Single/Monthly{isAdmin && <span style={{ color: 'red' }}>*</span>}</FormLabel>
                                            <div className="form-input-container">
                                                <RadioGroup
                                                    className="radio-group" value={isMonthly ? "multi" : "single"}
                                                    onChange={(e) => {
                                                        setIsMonthly(e === "multi");
                                                        props.setFieldValue('isMonthly', (e === "multi"))
                                                    }}
                                                    isDisabled={isSubmitting || !isAdmin}
                                                >
                                                    <Radio value="single"><span className="radio-option">Single</span></Radio>
                                                    <Radio value="multi"><span className="radio-option">Monthly</span></Radio>
                                                </RadioGroup>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            {isMonthly
                            ? (<Field name='month'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.month && form.touched.month)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Review Month{isAdmin && <span style={{ color: 'red' }}>*</span>}</FormLabel>
                                            <div className="form-input-container">
                                                <MonthSelector onChange={(month, year) => handleChangeMonth(month, year, props)} month={selectedMonth} year={selectedYear} allowAllMonths={false} disabled={isSubmitting || !isAdmin} />
                                                <FormErrorMessage>{String(form.errors.month)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>)
                            : (<Field name='review'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.reviewID && form.touched.reviewID)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Order Number{isAdmin && <span style={{ color: 'red' }}>*</span>}</FormLabel>
                                            <div className="form-input-container">
                                                <SelectInvoices name='reviewID' handleChange={(e: Option) => handleChangeReview(e, props)} value={singleReviewId} options={listOrderNumbers} hasError={Boolean(form.errors.reviewID && form.touched.reviewID)} isDisabled={isSubmitting || !isAdmin} />
                                                <FormErrorMessage>
                                                    {typeof form.errors.reviewID === 'string'
                                                        ? form.errors.reviewID
                                                        : Array.isArray(form.errors.reviewID)
                                                            ? form.errors.reviewID.join(', ')
                                                            : form.errors.reviewID
                                                                ? JSON.stringify(form.errors.reviewID)
                                                                : ''}
                                                </FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>)
                            }
                            <Field name='reviewCost'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.reviewCost && form.touched.reviewCost)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Cost per Review<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <CurrencyInput
                                                    {...field}
                                                    placeholder='Cost per Review'
														prefix={CURRENCY.USD.code}
                                                    value={props.values.reviewCost}
                                                    error={Boolean(form.errors.reviewCost && form.touched.reviewCost)}
                                                    onValueChange={({ floatValue = 0 }) => handleChangeReviewCost(floatValue, props.setFieldValue)}
                                                    disabled={isSubmitting || role === ROLES.FINANCE}
                                                />
                                                <FormErrorMessage>{String(form.errors.reviewCost)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='totalReviews'>
                                {({ field }: FieldProps) => (
                                    <FormControl style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Total Reviews</FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} value={totalReviews} type="number" placeholder='Total Reviews' className="form-input" isDisabled />
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='downloadCost'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.downloadCost && form.touched.downloadCost)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Cost per Download</FormLabel>
                                            <div className="form-input-container">
                                                <CurrencyInput
                                                    {...field}
                                                    placeholder='Cost per Download'
														prefix={CURRENCY.USD.code}
                                                    value={props.values.downloadCost}
                                                    onValueChange={({ floatValue = 0 }) => handleChangeSecondDownloadCost(floatValue, props.setFieldValue)}
                                                    disabled={isSubmitting || role === ROLES.FINANCE}
                                                />
                                                <FormErrorMessage>{String(form.errors.reviewCost)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='totalSecondDownload'>
                                {({ field }: FieldProps) => (
                                    <FormControl style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Total 2nd Download</FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} value={totalSecondDownload} type="number" placeholder='Total 2nd Download' className="form-input" isDisabled />
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='valueOfUSD'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.valueOfUSD && form.touched.valueOfUSD)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Exchange Rate</FormLabel>
                                            <div className="form-input-container">
                                                <CurrencyInput
                                                    {...field}
                                                    placeholder='Value of 1 USD in IDR'
														prefix={CURRENCY.IDR.code}
                                                    value={kurs}
                                                    error={Boolean(form.errors.valueOfUSD && form.touched.valueOfUSD)}
                                                    onValueChange={({ floatValue = 0 }) => handleChangeKurs(floatValue, props.setFieldValue)}
                                                    width="100%"
                                                    disabled
                                                />
                                                <FormErrorMessage>{String(form.errors.reviewCost)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='subtotal'>
                                {({ field }: FieldProps) => (
                                    <FormControl style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Subtotal</FormLabel>
                                            <div className="form-input-container">
                                                <CurrencyInput
                                                    {...field}
                                                    placeholder='Subtotal'
														prefix={CURRENCY.IDR.code}
                                                    value={subtotal}
                                                    disabled
                                                />
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='tax'>
                                {({ field }: FieldProps) => (
                                    <FormControl style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">{taxPercent}% Tax</FormLabel>
                                            <div className="form-input-container">
                                                <CurrencyInput
                                                    {...field}
                                                    placeholder='Value Added Tax (PPN)'
														prefix={CURRENCY.IDR.code}
                                                    value={taxAmount}
                                                    disabled
                                                />
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='totalCost'>
                                {({ field }: FieldProps) => (
                                    <FormControl style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Total Cost</FormLabel>
                                            <div className="form-input-container">
                                                <CurrencyInput
                                                    {...field}
                                                    placeholder='Total Cost'
														prefix={CURRENCY.IDR.code}
                                                    value={totalCost}
                                                    disabled
                                                />
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            {role === ROLES.FINANCE && <Field name='faktur'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.faktur && form.submitCount)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Tax Invoice (Faktur)<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <EditFile
                                                    isDisabled={isSubmitting}
                                                    isEditable
                                                    isInvalid={Boolean(form.errors.faktur && form.submitCount)}
                                                    handleDrop={(e) => handleDrop(e, 'faktur', props.setFieldValue)}
                                                    title="Tax Invoice (Faktur)"
                                                    type="faktur"
                                                />
                                                <FormErrorMessage>{String(form.errors.faktur)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>}

                            <Suspense fallback={<Loader />}>
                                <TableSireReviewSimple
                                    dataList={listSireReview}
                                    isLoading={isLoading}
                                    currentPageSize={currentPageSize}
                                    handleChangePageSize={setCurrentPageSize}
                                    handleChangePageNumber={setCurrentPageNumber}
                                    currentPageNumber={currentPageNumber}
                                    totalPageNumber={totalPageNumber}
                                    hasNextPage={hasNextPage}
                                    role={role}
                                />
                                {listSireReview.length === 0 && !isLoading && <span>No suitable SIRE reviews. Please select a different company or month.</span>}
                            </Suspense>

                            <Divider />
                            <div style={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 10 }}>
                                <Button
                                    mt={4}
                                    bgColor='white'
                                    borderColor='#D0D5DD'
                                    isLoading={isSubmitting}
                                    marginRight={5}
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </Button>
                                {isAdmin && <Button
                                    mt={4}
                                    bgColor='white'
                                    borderColor='#D0D5DD'
                                    isLoading={isSubmitting}
                                    marginRight={5}
                                    onClick={() => setIsDraft(true)}
                                    type='submit'
                                >
                                    Save as Draft
                                </Button>}
                                <Button
                                    mt={4}
                                    bgColor={COLORS.BUTTON.BACKGROUND}
                                    color={COLORS.BUTTON.FOREGROUND}
                                    isLoading={isSubmitting}
                                    // onClick={() => handleSubmit(initialContents)}
                                    type='submit'
                                    rightIcon={<ArrowIcon />}
                                >
                                    Send Invoice
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
            <ModalInvoicePreview
                isOpen={isPreviewOpen}
                onClose={() => setIsPreviewOpen(false)}
                invoiceData={buildInvoicePreviewData()}
                onConfirm={handlePreviewConfirm}
            />
        </>
    )
}

export default AddInvoicesSireRequestor;
